// src/pages/Acts.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { debounce } from "lodash";
import { listActs } from "../api/acts";
import { Button } from "../components/ui/Button";
import ActDetailsModal from "./ActDetailsModal";

/* ===== Иконки ===== */
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
      clipRule="evenodd"
    />
  </svg>
);
const PdfIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M6 2h8l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
    <path d="M14 2v4h4" />
  </svg>
);
const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M10 3C5 3 1.73 7.11 1 10c.73 2.89 4 7 9 7s8.27-4.11 9-7c-.73-2.89-4-7-9-7zm0 12a5 5 0 110-10 5 5 0 010 10z" />
    <circle cx="10" cy="10" r="3" fill="currentColor" />
  </svg>
);
const SpinnerIcon = () => (
  <svg
    className="animate-spin h-5 w-5 text-gray-500"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    ></path>
  </svg>
);

/* ===== Страница ===== */
export default function ActsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [previewingAct, setPreviewingAct] = useState(null);

  const fetchRows = useCallback(async (filters, currentPage, size) => {
    setLoading(true);
    try {
      // If size is not passed (from old calls), fallback to current state or default
      const limit = size || pageSize;
      const offset = (currentPage - 1) * limit;
      const { rows, count } = await listActs({
        ...filters,
        limit,
        offset,
      });
      setRows(rows || []);
      setCount(count || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetch = useCallback(
    debounce((filters, page, size) => fetchRows(filters, page, size), 300),
    [fetchRows]
  );

  useEffect(() => {
    const filters = { q, type, dateFrom, dateTo };
    if (page === 1) debouncedFetch(filters, 1, pageSize);
    else setPage(1);
  }, [q, type, dateFrom, dateTo, pageSize, debouncedFetch]);

  useEffect(() => {
    const filters = { q, type, dateFrom, dateTo };
    fetchRows(filters, page, pageSize);
  }, [page, pageSize, fetchRows]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / pageSize)),
    [count, pageSize]
  );
  const isFiltersActive = q || type || dateFrom || dateTo;

  const clearFilters = () => {
    setQ("");
    setType("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-screen-xl mx-auto space-y-8">
        <header>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100">
            Dalolatnomalar tarixi
          </h1>
          <p className="mt-1 text-md text-gray-600 dark:text-slate-400">
            Barcha dalolatnomalar arxivi. Ochish, chop etish yoki PDF sifatida saqlash mumkin.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div className="lg:col-span-2 relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Qidiruv
            </label>
            <div className="absolute inset-y-0 left-0 pl-3 pt-7 flex items-center pointer-events-none">
              <span className="text-gray-400 dark:text-slate-500">
                <SearchIcon />
              </span>
            </div>
            <input
              type="text"
              placeholder="Akt №, F.I.O, kontragent..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Turi
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 rounded-lg shadow-sm py-2 px-3 text-gray-900 dark:text-slate-100"
            >
              <option value="">Barcha turlar</option>
              <option value="in">Kirim</option>
              <option value="out">Chiqim</option>
              <option value="return">Qaytarish</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Sana (dan)
            </label>
            <input
              type="date"
              value={dateFrom ? dateFrom.substring(0, 10) : ""}
              onChange={(e) =>
                setDateFrom(
                  e.target.value ? new Date(e.target.value).toISOString() : ""
                )
              }
              className="w-full bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 rounded-lg shadow-sm py-2 px-3 text-gray-900 dark:text-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Sana (gacha)
            </label>
            <input
              type="date"
              value={dateTo ? dateTo.substring(0, 10) : ""}
              onChange={(e) =>
                setDateTo(
                  e.target.value ? new Date(e.target.value).toISOString() : ""
                )
              }
              className="w-full bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 rounded-lg shadow-sm py-2 px-3 text-gray-900 dark:text-slate-100"
            />
          </div>
          {isFiltersActive && (
            <Button variant="ghost" onClick={clearFilters} className="h-10 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800">
              Tozalash
            </Button>
          )}
        </div>

        <div>
          {loading ? (
            <SkeletonLayout />
          ) : rows.length === 0 ? (
            <EmptyState />
          ) : (
            <ActsTable rows={rows} onPreview={setPreviewingAct} />
          )}

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={count}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      </div>

      <ActDetailsModal
        open={!!previewingAct}
        act={previewingAct}
        onClose={() => setPreviewingAct(null)}
        typeLabel={(t) =>
          t === "in"
            ? "Kirim"
            : t === "out"
              ? "Chiqim"
              : t === "return"
                ? "Qaytarish"
                : t
        }
        user={previewingAct?.app_user_name ? { full_name: previewingAct.app_user_name } : null}
      />
    </div>
  );
}

/* ===== Адаптивная Таблица актов ===== */
const ActsTable = ({ rows, onPreview }) => {
  const typeMap = {
    in: { label: "Kirim", color: "blue" },
    out: { label: "Chiqim", color: "yellow" },
    return: { label: "Qaytarish", color: "green" },
  };

  return (
    <div>
      {/* Мобильный вид: Карточки */}
      <div className="sm:hidden space-y-4">
        {rows.map((row) => (
          <div
            key={row.id}
            className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-4 space-y-3 shadow-sm"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-gray-800 dark:text-slate-200">
                  {row.act_no || `№${row.id}`}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {new Date(row.created_at).toLocaleString("ru-RU", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </p>
              </div>
              <Badge color={typeMap[row.act_type]?.color}>
                {typeMap[row.act_type]?.label || row.act_type}
              </Badge>
            </div>
            <div className="border-t border-gray-100 dark:border-slate-700 pt-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar
                  name={
                    row.act_type === 'in'
                      ? (row.counterparty || "Noma'lum yetkazib beruvchi")
                      : (row.app_user_name || row.counterparty)
                  }
                  photoUrl={row.act_type === 'in' ? null : row.app_user_photo_url}
                />
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 dark:text-slate-200 truncate">
                    {row.act_type === 'in'
                      ? (row.counterparty || "Noma'lum yetkazib beruvchi")
                      : (row.app_user_name || row.counterparty || "Tashqi kontragent")
                    }
                  </p>
                  <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
                    ? `Qabul qildi: ${row.app_user_name || "—"}`
                    : (row.app_user_email || (row.counterparty ? `Kontragent: ${row.counterparty}` : "Tashqi kontragent"))
                  </p>
                </div>
              </div>
              <div className="flex items-center flex-shrink-0">
                {row.pdf_url && (
                  <a
                    href={row.pdf_url}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100"
                    title="PDF ni ochish"
                  >
                    <PdfIcon />
                  </a>
                )}
                <button
                  onClick={() => onPreview(row)}
                  className="p-2 rounded-full text-gray-500 hover:bg-gray-100"
                  title="Ochish / Chop etish"
                >
                  <EyeIcon />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Десктопный вид: Таблица */}
      <div className="hidden sm:block overflow-hidden bg-white dark:bg-slate-800 rounded-lg border border-gray-200/75 dark:border-slate-700/75">
        <table className="w-full">
          <thead className="bg-gray-50/75 dark:bg-slate-700/50 border-b border-gray-200/75 dark:border-slate-700/75">
            <tr className="text-left text-xs uppercase text-gray-500 dark:text-slate-400 tracking-wider">
              <th className="py-3 px-6 font-semibold text-sm">Akt №</th>
              <th className="py-3 px-6 font-semibold text-sm">Turi</th>
              <th className="py-3 px-6 font-semibold text-sm w-1/3">
                Ishtirokchi
              </th>
              <th className="py-3 px-6 font-semibold text-sm">Sana</th>
              <th className="py-3 px-6 font-semibold text-sm text-right pr-6">
                Amallar
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {rows.map((row) => (
              <tr key={row.id} className="bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                <td className="py-4 px-6 font-semibold text-gray-800 dark:text-slate-200">
                  {row.act_no || `№${row.id}`}
                </td>
                <td className="py-4 px-6">
                  <Badge color={typeMap[row.act_type]?.color}>
                    {typeMap[row.act_type]?.label || row.act_type}
                  </Badge>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={
                        row.act_type === 'in'
                          ? (row.counterparty || "Noma'lum yetkazib beruvchi")
                          : (row.app_user_name || row.counterparty)
                      }
                      photoUrl={row.act_type === 'in' ? null : row.app_user_photo_url}
                    />
                    <div>
                      <p className="font-medium text-gray-800 dark:text-slate-200">
                        {row.act_type === 'in'
                          ? (row.counterparty || "Noma'lum yetkazib beruvchi")
                          : (row.app_user_name || row.counterparty || "Tashqi kontragent")
                        }
                      </p>
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        ? `Qabul qildi: ${row.app_user_name || "—"}`
                        : (row.app_user_email || (row.counterparty ? `Kontragent: ${row.counterparty}` : "Tashqi kontragent"))
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-6 text-gray-500 dark:text-slate-400">
                  {new Date(row.created_at).toLocaleString("ru-RU")}
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center justify-end gap-2">
                    {row.pdf_url ? (
                      <a
                        href={row.pdf_url}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 rounded-full text-gray-500 hover:bg-gray-100"

                        title="Mavjud PDF ni ochish"
                      >
                        <PdfIcon />
                      </a>
                    ) : (
                      <span className="p-2 text-gray-300" title="PDF yo'q">
                        <PdfIcon />
                      </span>
                    )}
                    <Button
                      variant="outline"
                      className="bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600"
                      onClick={() => onPreview(row)}
                    >
                      <EyeIcon />
                      <span className="hidden lg:inline ml-2">
                        Ochish / Chop etish
                      </span>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


/* ===== Вспомогательные UI ===== */
const Avatar = ({ name, photoUrl }) => {
  const initials =
    (name || "")
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-200 font-bold rounded-full text-md overflow-hidden">
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name || "User"}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Если фото не загрузилось, показываем инициалы
            e.target.style.display = "none";
            const parent = e.target.parentElement;
            if (parent && !parent.querySelector("span")) {
              const span = document.createElement("span");
              span.textContent = initials;
              parent.appendChild(span);
            }
          }}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
};
const Badge = ({ children, color = "gray" }) => {
  const colors = {
    green: "bg-green-100 text-green-800",
    gray: "bg-gray-100 text-gray-800",
    yellow: "bg-yellow-100 text-yellow-800",
    blue: "bg-blue-100 text-blue-800",
  };
  return (
    <span
      className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${colors[color]}`}
    >
      {children}
    </span>
  );
};
const EmptyState = () => (
  <div className="py-20 text-center bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-slate-700">
    <svg
      className="mx-auto h-12 w-12 text-gray-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
    <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-slate-100">Dalolatnomalar topilmadi</h3>
    <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
      Filtrlarni o'zgartirib ko'ring yoki tozalang.
    </p>
  </div>
);
const SkeletonLayout = () => (
  <div className="animate-pulse">
    {/* Скелетон для мобильных */}
    <div className="sm:hidden space-y-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-4 space-y-4">
          <div className="flex justify-between items-center">
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-24"></div>
              <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-32"></div>
            </div>
            <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded-full w-20"></div>
          </div>
          <div className="border-t pt-4 flex items-center justify-between">
            <div className="flex items-center gap-3 w-full">
              <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-slate-700"></div>
              <div className="space-y-2 w-3/4">
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-5/6"></div>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="h-8 w-8 rounded-full bg-gray-200"></div>
              <div className="h-8 w-8 rounded-full bg-gray-200"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
    {/* Скелетон для десктопа */}
    <div className="hidden sm:block bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
      <div className="h-12 bg-gray-50/75 dark:bg-slate-700/50 border-b border-gray-200/75 dark:border-slate-700/75" />
      <div className="divide-y divide-gray-100 dark:divide-slate-700">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center space-x-6 h-20 px-6">
            <div className="w-[10%] h-5 bg-gray-200 dark:bg-slate-700 rounded" />
            <div className="w-[10%] h-6 bg-gray-200 dark:bg-slate-700 rounded-full" />
            <div className="w-1/3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-slate-700" />
              <div className="w-full space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/5" />
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-2/5" />
              </div>
            </div>
            <div className="w-1/4 h-4 bg-gray-200 rounded" />
            <div className="flex-grow flex justify-end">
              <div className="h-10 bg-gray-200 rounded-lg w-36" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
const Pagination = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalCount);
  return (
    <div className="mt-4 flex flex-col sm:flex-row items-center justify-between text-sm gap-4">
      <div className="flex items-center gap-4">
        <p className="text-gray-600 dark:text-slate-400 whitespace-nowrap">
          <span className="font-medium text-gray-900 dark:text-slate-200">{totalCount > 0 ? from : 0}</span>-
          <span className="font-medium text-gray-900 dark:text-slate-200">{to}</span> dan <span className="font-medium text-gray-900 dark:text-slate-200">{totalCount}</span>
          {" "}ta ko'rsatilmoqda
        </p>
        <select
          value={pageSize}
          onChange={(e) => {
            onPageSizeChange(Number(e.target.value));
            onPageChange(1);
          }}
          className="bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 rounded-md text-sm py-1 pl-2 pr-6 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value={10}>10 / sah</option>
          <option value={20}>20 / sah</option>
          <option value={50}>50 / sah</option>
          <option value={100}>100 / sah</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </Button>
        <span className="text-gray-600 dark:text-slate-400 px-2">
          {" "}
          {currentPage} / {totalPages}{" "}
        </span>
        <Button
          variant="outline"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </Button>
      </div>
    </div>
  );
};
